import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { item_id, node_id } from '../common'
import { no_parent, TreeData, TreeNode } from '../tree-data/tree-data'
import { createTreeStateActions } from './actions'
import { EMPTY_TREE_DATA } from '../tree-data/tree-data'
import { TreeEffect } from './effects'
import uniqueId from 'lodash.uniqueid'

export type TreeClientState = {
  selectedItems: Set<item_id>
  editingStates: EditingState[]
  addingStates: AddingState[]
  lastSelectedNode: node_id | null
  checkedItems: Record<item_id, boolean>
  toggledItems: Record<string, Record<item_id, boolean>>
  expandedNodes: Record<node_id, boolean>
  effectQueue: TreeEffect[]
  changeQueue: ClientChange[]
}

export type ClientChange = {id: string, settled: () => void} & (
| {type: 'select-once-when-in-tree-data', itemId: item_id}
| {type: 'workaround for `assertNever` only required when union of one'}
)

export type AddingState = {
  addingStateId: string
  parentItemId: item_id | no_parent
  userData: unknown
}

export type EditingState = {
  nodeId: string
  userData: unknown
}

export type CheckState = "unchecked" | "checked" | "indeterminate"

export type TreeComputedState = {
  checkStateByNode: Record<node_id, CheckState>
  visibleNodeIds: node_id[]
}

export type TreeStoreActions = {
  setItemSelection: (selected: item_id[]) => void
  select: (id: node_id, mode: "single" | "toggle" | "range") => void
  toggleNodeChecked: (id: node_id, checked: boolean) => void
  setCheckedItems: (checkedIds: item_id[]) => void
  toggleNode: (toggleId: string, id: node_id, toggled: boolean) => void
  setToggledItems: (toggleId: string, toggledIds: item_id[]) => void
  toggleNodeExpanded: (id: node_id, expanded: boolean) => void
  startNodeEditing: (id: node_id, userData?: unknown) => void
  stopNodeEditing: (id: node_id) => void
  startAdding: (parentId: node_id | no_parent, userData?: unknown) => AddingState
  stopAdding: (addingStateId: string) => void
  selectOnceWhenInTreeData: (nodeId: node_id) => void
  revealNode: (id: node_id, scrollBehaviour: "instant" | "smooth") => void
  computeCheckState: () => void
  computeVisibleNodeIds: () => void
  applyClientChangesInQueue: () => void
}

export type TreeStoreType = {
  id: string
  treeData: TreeData
  client: TreeClientState
  computed: TreeComputedState
  actions: TreeStoreActions
}

export type TreeStore = ReturnType<typeof createTreeStore>

export function createTreeStore() {
  return create<TreeStoreType>()(subscribeWithSelector((set, get, store) => ({
    id: uniqueId('tree-store-'),
    treeData: EMPTY_TREE_DATA,

    client: {
      selectedItems: new Set(),
      editingStates: [],
      addingStates: [],
      lastSelectedNode: null,
      checkedItems: {},
      toggledItems: {},
      expandedNodes: {},
      effectQueue: [],
      changeQueue: [],
    },

    computed: {
      checkStateByNode: {},
      visibleNodeIds: []
    },

    actions: createTreeStateActions()(set, get, store)
  })))
}

export function findNodeIdsByItemId(nodesById: Record<node_id, TreeNode>, itemId: item_id) {
  if (!itemId) return []
  const nodes: node_id[] = []
  for (const nodeId in nodesById) {
    if (!(nodeId in nodesById)) continue
    const node = nodesById[nodeId]
    if (node.itemId !== itemId) continue
    nodes.push(node.id)
  }
  return nodes
}

export function nodeIdToItemId(treeData: TreeData, nodeId: node_id, withLog = true): item_id | null {
  const node = treeData.nodesById[nodeId]
  if (!node) {
    if (withLog) console.warn(`Detected inconsistent state: no node found for node-id "${nodeId}"`)
    return null
  }
  return node.itemId
}

export function nodeIdToItemIdThrows(treeData: TreeData, nodeId: node_id): item_id {
  const itemId = nodeIdToItemId(treeData, nodeId, false)
  if (!itemId) throw new Error(`Detected inconsistent state: no item-id found for node-id "${nodeId}"`)
  return itemId
}

export function nodeIdsToItemIds(treeData: TreeData, nodeIds: Iterable<node_id>): item_id[] {
  const result: item_id[] = []
  for (const nodeId of nodeIds) {
    const itemId = nodeIdToItemId(treeData, nodeId)
    if (!itemId) continue
    result.push(itemId)
  }
  return result
}

export type ToggledItems = Record<string, Record<item_id, boolean>>

/** Toggle state of a single item: the client state if the user touched it, the query's default otherwise */
export function isItemToggled(
  treeData: TreeData,
  toggledItems: ToggledItems,
  toggleId: string,
  itemId: item_id,
): boolean {
  const byItem = toggledItems[toggleId]
  if (byItem && itemId in byItem) return byItem[itemId]
  return treeData.itemsById[itemId]?.defaultToggles[toggleId] ?? false
}

export function getToggledItemIds(treeData: TreeData, toggledItems: ToggledItems, toggleId: string): item_id[] {
  return Object.keys(treeData.itemsById)
    .filter(itemId => isItemToggled(treeData, toggledItems, toggleId, itemId))
}

export function getCheckedNodeIds(treeData: TreeData, checkState: Record<node_id, CheckState>, leavesOnly?: boolean): node_id[] {
  const nodeIds: node_id[] = []
  for (const nodeId in checkState) {
    if (!(nodeId in checkState)) continue
    if (checkState[nodeId] !== "checked") continue
    if (leavesOnly) {
      const node = treeData.nodesById[nodeId]
      if (!node) continue
      if (node.childrenIds.length > 0) continue
    }
    nodeIds.push(nodeId)
  }
  return nodeIds
}